import {Injectable, Injector} from '@angular/core';

import {Observable} from 'rxjs';
import {HttpService} from 'projects/core/src/public-api';
import {AppStateStore} from 'projects/desktop/src/app/data/app-state.store';
import {AppStateQuery} from 'projects/desktop/src/app/data/app-state.query';
import {FileService} from 'projects/core/src/lib/misc/data/file/file.service';
import {EmailCondition} from './email-condition';
import {SystemDictionary} from './system-dictionary';
import {TemporaryVariable} from './temporary-variable';
import {RoleAccess} from 'projects/core/src/lib/login/data/role-access';
import {ColumnForEdit} from './column-for-edit';
import {ActivityForEdit} from './activityi-for-edit';
import {NotificationCondition} from 'projects/app-misc/src/app/modules/admin-panel/data/nfs-condition';
import {NfsSubscription} from 'projects/app-misc/src/app/modules/admin-panel/data/nfs-sub';
import {NfsSubscriptionType} from 'projects/app-misc/src/app/modules/admin-panel/data/nfs-sub-type';
import {ItemGroup} from 'projects/app-misc/src/app/modules/admin-panel/data/item-group';
import {PricingGrade} from '../../project-calc/data/pricing-grade';
import {PricingCategory} from '../../project-calc/data/pricing-category';
import {CategoryModifier} from '../../project-calc/data/category-modifier';
import {ShortObject} from '../../project-misc/data/project/short-json-object';
import {PricingMultiplication} from '../../project-calc/data/multiplication/pricing-multiplication';
import {PricingMultiplicationTypes} from '../../project-calc/data/multiplication/pricing-multiplication-type';
import {PricingFee} from '../../project-calc/data/fee/pricing-fee';
import {PricingFeeTypes} from '../../project-calc/data/fee/pricing-fee-types';
import {ProjectFile} from '../../project-misc/data/project/project-file';
import {HttpEvent} from '@angular/common/http';
import {Item} from './item';
import {ProductionPlanningPost} from '../../production-planning/data/production-planning-post';
import {NfsAlertCategory} from './nfs-alert-category';
import {ProductionPlanningWeek} from '../../production-planning/data/production-planning-week';
import {Preset} from './preset';

@Injectable({
  providedIn: 'root',
})
export class AdminPanelService extends HttpService {
  dictsMap = new Map([
    ['NOTIFICATION_CONDITION', 'System Notifications'],
    ['PROJECT_ALERT_CONDITION', 'Project Alerts'],
    ['WARNING_CONDITION', 'Warning Notifications'],
    ['CV_JOB_INFO_ALERT_CONDITION', 'CV Notifications'],
    ['CV_CLIENT_NOTIFICATION_CONDITION', 'CV Client Notifications'],
    ['PROJECT_STATUS', 'Project Status'],
    ['PROJECT_TYPE', ' Project Type'],
    ['PROJECT_COMPLEXITY', 'Project Complexity'],
    ['PAYMENT_STATUS', 'Payment Status'],
    ['CV_CABINET_STATUS', 'Cv Cabinet Status'],
    ['PURCHASE_ORDER_STATUS', 'Purchase Order Status'],
    ['DOCUSIGN_STATUS', 'Docusign Status'],
    ['CLIENT_NFS_STATUS', 'Client Nfs Status'],
    ['IQ_CLIENT_NOTIFICATION_CONDITION', 'Installation Quotes'],
    ['CLIENT_NOTIFICATION_CONDITION', 'Client Notifications'],
    ['SERVICE_CLIENT_NOTIFICATION_CONDITION', 'Service Client Notifications'],
    ['SYSTEM_ACCESS', 'System Access'],
    ['ACTIVITIES_CONDITION', 'Schedule Activities'],
    ['COLUMNS_CONDITION', 'Kanban Board Columns']])

  constructor(
    protected injector: Injector,
    protected appStateStore: AppStateStore,
    protected appStateQuery: AppStateQuery,
    protected fileService: FileService
  ) {
    super(injector);
  }

  addConditionSub(conditionId: number, sub: NfsSubscription): Observable<NfsSubscription> {
    return this.postData(`/notification-condition/${conditionId}/subscriptions`, sub, NfsSubscription)
  }

  getCategorizedSystemNfsAndAlertsConditions(): Observable<NfsAlertCategory[]> {
    return this.getData(`/notification-condition/list/categorized`, NfsAlertCategory);
  }

  getClientsConditions(): Observable<EmailCondition[]> {
    return this.getData(`/notification-condition/list/for-clients`, EmailCondition);
  }

  getSystemDictionaries(): Observable<SystemDictionary[]> {
    return this.getData(`/dictionary-word/list`, SystemDictionary);
  }

  getConditionTemplates(clazz: string): Observable<TemporaryVariable[]> {
    return this.getData(`/notification-condition/templates?clazz=${clazz}`, TemporaryVariable);
  }


  getColumnConditions(): Observable<ColumnForEdit[]> {
    return this.getData(`/column/all/for-edit`, ColumnForEdit);
  }

  getActivityConditions(): Observable<ActivityForEdit[]> {
    return this.getData(`/activity/types`, ActivityForEdit);
  }

  getProductionPosts(): Observable<ProductionPlanningPost[]> {
    return this.getData(`/production-planning/post/list`, ProductionPlanningPost)
  }

  getConditionSubs(conditionId: number): Observable<NfsSubscription[]> {
    return this.getData(`/notification-condition/${conditionId}/subscriptions`, NfsSubscription);
  }

  getConditionSubTypes(): Observable<NfsSubscriptionType[]> {
    return this.getData(`/notification-condition/subscriptions/types`, NfsSubscriptionType);
  }

  getGroupItems(): Observable<ItemGroup[]> {
    return this.getData(`/pricing-settings/misc-groups`, ItemGroup);
  }

  getMiscGroupNames(): Observable<ShortObject[]> {
    return this.getData(`/pricing-settings/misc-groups/names`, ShortObject);
  }

  createGroup(group: ItemGroup): Observable<ItemGroup> {
    return this.postData(`/pricing-settings/misc-groups`, group, ItemGroup);
  }

  updateGroupItems(group: ItemGroup): Observable<ItemGroup> {
    return this.putData(`/pricing-settings/misc-groups`, group, ItemGroup);
  }

  deleteGroup(groupId: number): Observable<ItemGroup> {
    return this.deleteData(`/pricing-settings/misc-groups/${groupId}`, ItemGroup);
  }

  deleteItem(groupId: number, itemId: number): Observable<ItemGroup> {
    return this.deleteData(`/pricing-settings/misc-groups/${groupId}/items/${itemId}`, ItemGroup);
  }

  saveClientsCondition(emailCondition: EmailCondition): Observable<EmailCondition> {
    return this.putData('/notification-condition', emailCondition, EmailCondition)
  }

  saveSystemDictionary(systemDictionary: SystemDictionary): Observable<SystemDictionary> {
    return this.putData('/dictionary-word/list/update', systemDictionary, SystemDictionary)
  }

  saveRoleAccess(roleAccess: RoleAccess): Observable<RoleAccess> {
    return this.putData('/page-view', roleAccess, RoleAccess)
  }

  createRoleAccess(roleAccess: RoleAccess): Observable<RoleAccess> {
    return this.postData(`/page-view`, roleAccess, RoleAccess);
  }

  saveColumnCondition(columnCondition: ColumnForEdit): Observable<ColumnForEdit> {
    return this.putData('/column/all/for-edit/update', columnCondition, ColumnForEdit)
  }

  saveActivityCondition(activityCondition: ActivityForEdit): Observable<ActivityForEdit> {
    return this.putData('/activity/types', activityCondition, ActivityForEdit)
  }

  saveProductionPost(post: ProductionPlanningPost): Observable<ProductionPlanningPost> {
    return this.putData('/production-planning/post', post, ProductionPlanningPost)
  }

  saveProductionWeek(week: ProductionPlanningWeek): Observable<ProductionPlanningWeek> {
    return this.putData('/production-planning/weeks', week, ProductionPlanningWeek)
  }

  saveConditionSub(conditionId: number, sub: NfsSubscription): Observable<NfsSubscription> {
    return this.putData(`/notification-condition/${conditionId}/subscriptions`, sub, NfsSubscription)
  }

  deleteConditionSub(conditionId: number, subId: number): Observable<ActivityForEdit> {
    return this.deleteData(`/notification-condition/${conditionId}/subscriptions/${subId}`, ActivityForEdit)
  }

  // GRADES
  getGrades(): Observable<PricingGrade[]> {
    return this.getData(`/pricing-settings/grades`, PricingGrade);
  }

  getGradeTypes(): Observable<PricingGrade[]> {
    return this.getData(`/pricing-settings/grades/types`, PricingGrade);
  }

  updateGrade(grade: PricingGrade): Observable<PricingGrade> {
    return this.putData(`/pricing-settings/grades`, grade, PricingGrade);
  }

  // CATEGORIES
  createCategory(category: PricingCategory): Observable<PricingCategory> {
    return this.postData(`/pricing-settings/categories`, category, PricingCategory);
  }

  getCategories(): Observable<PricingCategory[]> {
    return this.getData(`/pricing-settings/categories`, PricingCategory);
  }

  getShortCategories(): Observable<ShortObject[]> {
    return this.getData(`/pricing-settings/categories/short?enabled=true`, ShortObject);
  }

  updateCategory(category: PricingCategory): Observable<PricingCategory> {
    return this.putData(`/pricing-settings/categories`, category, PricingCategory);
  }

  deleteCategory(categoryId: number): Observable<PricingCategory> {
    return this.deleteData(`/pricing-settings/categories/${categoryId}`, PricingCategory);
  }

  // MODIFIERS
  getModifiers(): Observable<CategoryModifier[]> {
    return this.getData(`/pricing-settings/modifiers`, CategoryModifier);
  }

  getModifiersForCategory(categoryIds: number[]): Observable<CategoryModifier[]> {
    return this.getData(`/pricing-settings/modifiers?categoryIds=${categoryIds}`, CategoryModifier);
  }

  createModifier(modifier: CategoryModifier): Observable<CategoryModifier> {
    return this.postData(`/pricing-settings/modifiers`, modifier, CategoryModifier);
  }

  updateModifier(modifier: CategoryModifier): Observable<CategoryModifier> {
    return this.putData(`/pricing-settings/modifiers`, modifier, CategoryModifier);
  }

  deleteModifier(modifierId: number): Observable<CategoryModifier> {
    return this.deleteData(`/pricing-settings/modifiers/${modifierId}`, CategoryModifier);
  }

  // MULTIPLICATIONS
  getMultiplications(): Observable<PricingMultiplication[]> {
    return this.getData(`/pricing-settings/multiplications`, PricingMultiplication);
  }

  getMultiplicationTypes(): Observable<PricingMultiplicationTypes> {
    return this.getData(`/pricing-settings/multiplications/types`, PricingMultiplicationTypes);
  }

  createMultiplication(multiplication: PricingMultiplication): Observable<PricingMultiplication> {
    return this.postData(`/pricing-settings/multiplications`, multiplication, PricingMultiplication);
  }

  updateMultiplication(multiplication: PricingMultiplication): Observable<PricingMultiplication> {
    return this.putData(`/pricing-settings/multiplications`, multiplication, PricingMultiplication);
  }

  deleteMultiplication(multiplicationId: number): Observable<PricingMultiplication> {
    return this.deleteData(`/pricing-settings/multiplications/${multiplicationId}`, PricingMultiplication);
  }

  // FEES
  getFeeList(): Observable<PricingFee[]> {
    return this.getData(`/pricing-settings/fee/list`, PricingFee);
  }

  getFeeTypes(): Observable<PricingFeeTypes> {
    return this.getData(`/pricing-settings/fee/settings`, PricingFeeTypes);
  }

  createFee(fee: PricingFee): Observable<PricingFee> {
    return this.postData(`/pricing-settings/fee`, fee, PricingFee);
  }

  updateFee(fee: PricingFee): Observable<PricingMultiplication> {
    return this.putData(`/pricing-settings/fee`, fee, PricingFee);
  }

  deleteFee(feeId: number): Observable<PricingMultiplication> {
    return this.deleteData(` /pricing-settings/fee/${feeId}`, PricingMultiplication);
  }

  getMiscFiles(miscId: number): Observable<ProjectFile[]> {
    return this.getData(this.fileService.getListFilesLink('misc', miscId), ProjectFile);
  }

  uploadMiscFiles(files: File[], miscId: number): Observable<HttpEvent<ProjectFile[]>> {
    return this.fileService.postFile<ProjectFile[]>(this.fileService.getUploadFileLink('misc', miscId), 'files', files);
  }

  deleteMiscFile(fileId: number): Observable<ProjectFile> {
    return this.deleteData(this.fileService.getDeleteFileLink(fileId), ProjectFile)
  }

  createNewMisc(misc: Item): Observable<Item> {
    return this.postData(`/pricing-settings/misc`, misc, Item);
  }

}
